import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';

import {
  Divider,
  Icon,
  Grid,
  Segment,
  Popup,
  Image,
  Label,
  Table,
} from 'semantic-ui-react';

import DemographicsAgeGraph from 'dyzio-shared-components/lib/components/demographics-age-graph';
import DemographicsGenderGraph from 'dyzio-shared-components/lib/components/demographics-gender-graph';
import FollowersBarChart from 'dyzio-shared-components/lib/components/followers-bar-chart';
import FacebookLocationTabs from './location-tabs';
import FullCountryModal from './full-country-modal';
import FullCityModal from './full-city-modal';
import FullMetricModal from './full-metric-modal';
import hasUserInfo from '../../hocs/has-user-info';

import {
  commaFormatted,
} from '../../utils/helpers/formatting';

const noBottomPadding = {
  paddingBottom: 0,
};

const dividerStyle = {
  marginTop: 5,
  marginBottom: 5,
};

const textCenter = {
  textAlign: 'center',
};

const MODAL_LINK_STYLE = {
  cursor: 'pointer',
  ...textCenter,
};


const DYZIO_APP_ID = '116451675157336';

const FacebookProfileDataLarge = props => {
  const {
    displayAuthorisedData,
    handle,
    pageMetricModalOpen,
    facebookVerified,
    countHistory,
    pageMetrics,
    facebookCountryDemographicsModalOpen,
    facebookCityDemographicsModalOpen,
    toggleFacebookCountryDemographicsModalState,
    toggleFacebookCityDemographicsModalState,
    userInfo: {
      organisationId,
    },
    onFullPageMetrics,
  } = props;

  if (!handle) return null;

  const cleanHandle = _.replace(handle, 'https://www.facebook.com/', '');
  const facebookHistory = _.filter(countHistory, item => _.has(item, 'facebookCount'));
  const lastFBHistoryItem = _.last(facebookHistory);
  const { data: { engagements, views, impressions, consumptions, videos } } = pageMetrics || { data: {} };
  const usingOldFormat = !!(_.last(_.get(engagements, 'values'))); /* TODO(Kai): Depricate then remove this line */
  const graphProps = {
    label: 'followers',
    labels: _.map(facebookHistory, item => moment(item.createdDate).format('MMM D')),
    values: _.map(facebookHistory, 'facebookCount'),
    rgbColorCode: '#3B5998',
  };

  const lastFacebookCount = _.get(lastFBHistoryItem, 'facebookCount', null);
  return (
    <Segment color="blue">
      <Grid>
        <Grid.Row>
          <Grid.Column width={16}>
            <h3>
              <Icon name="facebook" color="blue" />
                Facebook
            </h3>
            <Divider />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={3}>
            <Image
              src={`https://graph.facebook.com/v2.11/${cleanHandle}/picture?redirect=1&app_id=${DYZIO_APP_ID}`}
              shape="circular"
              centered
              size="small"
            />
            <Table basic="very" fluid textAlign="center">
              <Table.Body>
                <Table.Row>
                  <Table.Cell>
                    { cleanHandle }
                    { facebookVerified && <Icon name="check circle" color="blue" /> }
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>{ commaFormatted(lastFacebookCount) } followers</Table.Cell>
                </Table.Row>
                {
                  !facebookVerified &&
                  <Table.Row>
                    <Table.Cell>
                      <Popup
                        trigger={<Label basic color="grey" content="not verified" />}
                        content={`${cleanHandle} is not verified by Facebook (blue tick)`}
                      />
                    </Table.Cell>
                  </Table.Row>
                }
              </Table.Body>
            </Table>
          </Grid.Column>
          <Grid.Column width={8}>
            <FollowersBarChart
              {...graphProps}
              percentageRange={0.5}
            />
          </Grid.Column>
          {
            <Grid.Column width={5}>
              <h4 style={textCenter}>Last 28 days page insights</h4>
              <Table basic="very" fluid>
                <Table.Row>
                  <Table.Cell>total page followers</Table.Cell>
                  <Table.Cell>{ commaFormatted(lastFacebookCount) }</Table.Cell>
                </Table.Row>
                {!usingOldFormat && displayAuthorisedData && engagements &&
                <Table.Row>
                  <Table.Cell>
                    <Popup
                      trigger={<span>posts engagements <Icon name="info circle" color="blue" /></span>}
                      content="The number of times people have engaged with page
                          posts through like, comments and shares and more."
                    />
                  </Table.Cell>
                  <Table.Cell>{ commaFormatted(engagements.value) }</Table.Cell>
                </Table.Row>
                }
                {!usingOldFormat && displayAuthorisedData && views &&
                <Table.Row>
                  <Table.Cell>
                    <Popup
                      trigger={<span>views <Icon name="info circle" color="blue" /></span>}
                      content="Total views count per Page"
                    />
                  </Table.Cell>
                  <Table.Cell>{ commaFormatted(views.value) }</Table.Cell>
                </Table.Row>
                }
                {!usingOldFormat && displayAuthorisedData && impressions &&
                <Table.Row>
                  <Table.Cell>
                    <Popup
                      trigger={<span>impressions <Icon name="info circle" color="blue" /></span>}
                      content="The number of times your Page's posts entered a person's screen.
                      Posts include statuses, photos, links, videos and more. (Total Count)"
                    />
                  </Table.Cell>
                  <Table.Cell>{ commaFormatted(impressions.value) }</Table.Cell>
                </Table.Row>
                }
                {!usingOldFormat && displayAuthorisedData && consumptions &&
                <Table.Row>
                  <Table.Cell>
                    <Popup
                      trigger={<span>consumptions <Icon name="info circle" color="blue" /></span>}
                      content="The number of clicks on any of page content.
                      Stories generated without clicks on page content
                      (e.g., liking the page in Timeline) are not included. (Total Count)"
                    />
                  </Table.Cell>
                  <Table.Cell>{ commaFormatted(consumptions.value) }</Table.Cell>
                </Table.Row>
                }
                {!usingOldFormat && displayAuthorisedData && videos &&
                <Table.Row>
                  <Table.Cell>
                    <Popup
                      trigger={<span>videos engagements<Icon name="info circle" color="blue" /></span>}
                      content="Total number of times videos have been viewed for more than 3 seconds. (Total Count)"
                    />
                  </Table.Cell>
                  <Table.Cell>{ commaFormatted(videos.value) }</Table.Cell>
                </Table.Row>
                }
              </Table>
              {!usingOldFormat && displayAuthorisedData &&
                <div style={textCenter}>
                  <a style={MODAL_LINK_STYLE} onClick={onFullPageMetrics}>
                      View full page metric data
                  </a>
                </div>
              }
            </Grid.Column>
          }
        </Grid.Row>
        {
          displayAuthorisedData && props.audienceDemographics && props.audienceLocations &&
          <Grid.Row textAlign="center" style={noBottomPadding} divided>
            <Grid.Column width={16}>
              <Divider fitted style={dividerStyle} />
              <h2>
                Audience Demographics and Regions
                <Popup
                  trigger={<Icon name="lock" color="orange" />}
                  content="Private data collected from Facebook API"
                />
              </h2>
              <Divider fitted style={dividerStyle} />
            </Grid.Column>
            <Grid.Column width={4} verticalAlign="middle">
              <h5>Viewing by Gender</h5>
              <DemographicsGenderGraph {...props} />
            </Grid.Column>
            <Grid.Column width={7} verticalAlign="middle">
              <h5>Channel demographic by gender / age</h5>
              <DemographicsAgeGraph {...props} />
            </Grid.Column>
            <Grid.Column width={5}>
              <FacebookLocationTabs
                {...props}
                onFullCityClick={toggleFacebookCityDemographicsModalState}
                onFullCountryClick={toggleFacebookCountryDemographicsModalState}
              />
            </Grid.Column>
          </Grid.Row>
        }
        {
          facebookCountryDemographicsModalOpen &&
            <FullCountryModal {...props} onToggle={toggleFacebookCountryDemographicsModalState} />
        }
        {
          facebookCityDemographicsModalOpen &&
            <FullCityModal {...props} onToggle={toggleFacebookCityDemographicsModalState} />
        }
        {
          pageMetricModalOpen && pageMetrics &&
            <FullMetricModal {...pageMetrics.data} username={handle} onToggle={onFullPageMetrics} />
        }
      </Grid>
    </Segment>
  );
};

FacebookProfileDataLarge.propTypes = {
  handle: PropTypes.string.isRequired,
  facebookVerified: PropTypes.bool,
  countHistory: PropTypes.array.isRequired,
  audienceLocations: PropTypes.object,
  audienceDemographics: PropTypes.object,
  facebookCountryDemographicsModalOpen: PropTypes.bool,
  toggleFacebookCountryDemographicsModalState: PropTypes.func.isRequired,
  facebookCityDemographicsModalOpen: PropTypes.bool,
  toggleFacebookCityDemographicsModalState: PropTypes.func.isRequired,
  pageMetrics: PropTypes.object,
  onFullPageMetrics: PropTypes.func.isRequired,
  pageMetricModalOpen: PropTypes.bool,
  userInfo: PropTypes.object.isRequired,
  displayAuthorisedData: PropTypes.bool,
};

FacebookProfileDataLarge.defaultProps = {
  facebookVerified: false,
  audienceLocations: null,
  audienceDemographics: null,
  pageMetrics: null,
  facebookCountryDemographicsModalOpen: false,
  facebookCityDemographicsModalOpen: false,
  pageMetricModalOpen: false,
  displayAuthorisedData: false,
};

export default hasUserInfo(FacebookProfileDataLarge);



/** WEBPACK FOOTER **
 ** ./src/components/facebook-profile-data-large/index.js
 **/